export interface Stage {
  id: string;
  name: string;
  before: string;
  after: string;
  shift: string;
  benefit: string;
  risk: string;
}

export const lifecycle: Stage[] = [
  {
    id: 'ideation',
    name: 'Conception & ideation',
    before: 'Customer interviews, JTBD workshops, a strategy deck.',
    after:
      'Research-agent conversations against transcripts, tickets, win/loss notes; 20 candidate problems per hour; humans select.',
    shift:
      'Scarce ideas, abundant judgement → abundant ideas, scarce judgement.',
    benefit: 'Wider option space, faster falsification.',
    risk:
      'Convergent mediocrity across teams prompting the same models.',
  },
  {
    id: 'planning',
    name: 'Planning & roadmapping',
    before: 'Quarterly OKRs, RICE, capacity spreadsheets.',
    after:
      'Roadmaps maintained partly by agents watching trackers and telemetry; inference budget added to capacity planning.',
    shift: 'Plan-then-execute → continuously re-planned execution.',
    benefit: 'Shorter loops between strategy and delivery.',
    risk: '"Always re-planning" looks like "no plan" without governance.',
  },
  {
    id: 'spec',
    name: 'Spec & product/UX design',
    before:
      'Figma, design systems, usability testing, prescriptive PRDs.',
    after:
      'Dual UX: human UI + llms.txt + structured data + capability manifest. Specs as intent + constraints + evals.',
    shift: 'UX is a contract surface for humans and agents.',
    benefit:
      'Discoverable and usable by agentic clients (ChatGPT Apps, Claude, Perplexity, Comet).',
    risk:
      'Design only for humans → invisible to agents. Design only for agents → no brand.',
  },
  {
    id: 'arch',
    name: 'Architecture & technical design',
    before: 'Microservices, event streams, ADRs, Well-Architected.',
    after:
      'Adds cognition plane: model gateway, memory, MCP tools, agent runtime. ADRs record model choice and fallback.',
    shift: 'The system diagram grows a third plane.',
    benefit: 'Cohesive design for agentic workloads.',
    risk:
      'Single-model-provider lock-in is the new database lock-in — worse, because behaviour is proprietary.',
  },
  {
    id: 'dev',
    name: 'Development & coding',
    before: 'Humans write; reviewers gate via PR.',
    after:
      'Net-new code drafted by agents, curated by humans. Anthropic, Google, Microsoft report 30-50%+ agent-generated code in some teams. The scaling discipline is eval-gated PRs + AGENTS.md + MCP-everywhere + sandboxed execution.',
    shift:
      'Engineers move from authors to editors and reviewers of machine output.',
    benefit: '2-5× throughput on bounded work.',
    risk:
      'Review fatigue, hallucinated APIs, copy-pasted licensed code with attribution stripped.',
  },
  {
    id: 'qa',
    name: 'Testing & QA',
    before: 'Unit → integration → e2e. Test pyramid.',
    after:
      'Two new layers: evals (OpenAI Evals, DeepEval, Braintrust, Langfuse, Latitude) and red-teaming (DeepTeam). Bar moves from "tests pass" to "evals do not regress."',
    shift: 'Binary pass/fail → multi-dimensional scoring with thresholds.',
    benefit: 'Quality measured, not asserted.',
    risk: 'Bad evals shipped confidently are worse than no evals.',
  },
  {
    id: 'cicd',
    name: 'CI/CD & release management',
    before:
      'GitHub Actions / Argo / Buildkite. Feature flags. Canary, blue/green.',
    after:
      'Evals stage before deploy. Feature flags become capability gates routing between models, prompts, agent configs. Dynamic model fallback (Cloudflare AI Gateway, Portkey, LiteLLM).',
    shift: 'Ship a (code, prompt, model, eval) tuple, not just code.',
    benefit: 'Safer model upgrades; provider outages stop being product outages.',
    risk:
      'Prompt and model versions are supply-chain artefacts most orgs do not yet track.',
  },
  {
    id: 'launch',
    name: 'Launch & GTM',
    before: 'SEO, content marketing, paid acquisition.',
    after:
      'GEO. llms.txt, schema.org, citable claims, do not block AI crawlers. Princeton: GEO techniques lift AI-answer visibility 27-41%.',
    shift: 'Your audience now includes the model that recommends you.',
    benefit: 'Citations in AI answers compound into durable acquisition.',
    risk:
      'A brand can be invisible inside agentic clients while looking healthy in Search Console.',
  },
  {
    id: 'obs',
    name: 'Observability & monitoring',
    before: 'Logs, metrics, traces. Humans watch graphs.',
    after:
      'AI-native observability. OpenTelemetry GenAI semantic conventions v1.40.0 (Datadog, Honeycomb, New Relic, Grafana, Langfuse). Honeycomb Agent Timeline renders multi-agent traces as one timeline.',
    shift: 'Dashboards humans read → agents that read dashboards.',
    benefit: 'High-cardinality questions become tractable.',
    risk:
      'PII leak via prompt content in spans; default content capture off, redact at the Collector.',
  },
  {
    id: 'scale',
    name: 'Scaling & SRE',
    before: 'ASGs, multi-AZ, chaos engineering, SLOs.',
    after:
      'Token-budget SLOs, model-fallback SLOs, agent-step budgets. Cascading agent failures are a new incident class. AI Gateway pattern: cache, retry, fallback, buffered streaming.',
    shift: '"Is it up?" → "Is it cheap, fast, and grounded enough?"',
    benefit: 'Reliability defined in terms users feel.',
    risk:
      'Cost and quality enter the SLO surface; on-call reasons about three axes.',
  },
  {
    id: 'iterate',
    name: 'Iteration & continuous improvement',
    before: 'A/B tests, experimentation platforms, growth loops.',
    after:
      'Every production failure becomes an eval case. Prompt optimisation (DSPy, GEPA) iterates prompts the way we iterated code. Memory (Letta, Mem0) lets agents learn from their own sessions.',
    shift:
      'Experiment on prompts, models and agent configs — not just humans.',
    benefit: 'Quality improves continuously without code changes.',
    risk: 'Silent behavioural drift when upstream models update mid-cycle.',
  },
  {
    id: 'sec',
    name: 'Security & compliance',
    before: 'WAF, bot management, IAM, SBOM, SOC 2, ISO 27001.',
    after:
      'OWASP Top 10 for Agentic Applications (ASI 2026): goal hijack, indirect prompt injection, tool misuse, memory poisoning, rogue sub-agents. Agent identity via OAuth for agents, scoped per-task tokens, AI Gateways for egress, prompt-injection firewalls.',
    shift:
      'Treat every model as a hostile user; every agent as a service principal.',
    benefit: 'Defensible architecture against a named taxonomy.',
    risk:
      'Regulators are catching up; you will be asked to prove controls you do not yet have.',
  },
  {
    id: 'decom',
    name: 'Decommissioning & lifecycle',
    before: 'Sunset notices, data export, archive, delete.',
    after:
      'Model deprecation drills, prompt/eval archives, AIBOM retention (CycloneDX ML-BOM / SPDX 3.0). EU AI Act high-risk obligations enforceable 2 Aug 2026; ISO/IEC 42001 increasingly a procurement requirement.',
    shift: 'Delete code → retain provenance.',
    benefit: 'Audit and incident reconstruction remain possible years later.',
    risk:
      'Most teams have no AIBOM. Auditors will ask for what cannot be produced.',
  },
];
